/**
 * Universal Logger Sensitive Data Filter Engine
 * 
 * High-performance, enterprise-grade sensitive data filtering engine with
 * comprehensive pattern matching, caching, and monitoring capabilities.
 */

import {
  FilterRule,
  FilterRuleSet,
  FilterMatch,
  FilterResult,
  FilterError,
  FilterContext,
  FilterEngineConfig,
  FilterEngineStats,
  FilterCacheEntry,
  FilterAuditEntry,
  IFilterEngine,
  FilterRuleBuilder,
  SensitiveDataCategory,
  FilterRuleType,
  FilterAction,
  FilterPriority,
  FilterSeverity
} from './filter-types';

import { getAllPredefinedPatterns } from './filter-patterns';

/**
 * LRU Cache implementation for filter results
 */
class FilterCache {
  private cache = new Map<string, FilterCacheEntry>();
  private readonly maxSize: number;
  private readonly ttl: number;

  constructor(maxSize = 1000, ttl = 300000) { // 5 minutes default TTL
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  get(key: string): FilterMatch[] | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check TTL
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Update hit count and move to end (LRU)
    entry.hitCount++;
    this.cache.delete(key);
    this.cache.set(key, entry);
    
    return entry.result;
  }

  set(key: string, result: FilterMatch[], ruleVersion: string): void {
    // Remove oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value; if (firstKey) {
      this.cache.delete(firstKey); }
    }

    this.cache.set(key, {
      key,
      result,
      timestamp: Date.now(),
      hitCount: 0,
      ruleVersion
    });
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  getHitRate(): number {
    const entries = Array.from(this.cache.values());
    const totalAccesses = entries.reduce((sum, entry) => sum + entry.hitCount + 1, 0);
    const hits = entries.reduce((sum, entry) => sum + entry.hitCount, 0);
    return totalAccesses > 0 ? hits / totalAccesses : 0;
  }
}

/**
 * Filter Rule Builder Implementation
 */
class FilterRuleBuilderImpl implements FilterRuleBuilder {
  private rule: Partial<FilterRule> = {
    enabled: true,
    tags: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  setId(id: string): FilterRuleBuilder {
    this.rule.id = id;
    return this;
  }

  setName(name: string): FilterRuleBuilder {
    this.rule.name = name;
    return this;
  }

  setDescription(description: string): FilterRuleBuilder {
    this.rule.description = description;
    return this;
  }

  setCategory(category: SensitiveDataCategory): FilterRuleBuilder {
    this.rule.category = category;
    return this;
  }

  setType(type: FilterRuleType): FilterRuleBuilder {
    this.rule.type = type;
    return this;
  }

  setPattern(pattern: string | RegExp): FilterRuleBuilder {
    this.rule.pattern = pattern;
    return this;
  }

  setAction(action: FilterAction): FilterRuleBuilder {
    this.rule.action = action;
    return this;
  }

  setPriority(priority: FilterPriority): FilterRuleBuilder {
    this.rule.priority = priority;
    return this;
  }

  setSeverity(severity: FilterSeverity): FilterRuleBuilder {
    this.rule.severity = severity;
    return this;
  }

  setEnabled(enabled: boolean): FilterRuleBuilder {
    this.rule.enabled = enabled;
    return this;
  }

  addTag(tag: string): FilterRuleBuilder {
    if (!this.rule.tags) this.rule.tags = [];
    this.rule.tags.push(tag);
    return this;
  }

  setReplacement(replacement: string): FilterRuleBuilder {
    this.rule.replacement = replacement;
    return this;
  }

  setPreserveFormat(preserve: boolean): FilterRuleBuilder {
    this.rule.preserveFormat = preserve;
    return this;
  }

  setCaseSensitive(sensitive: boolean): FilterRuleBuilder {
    this.rule.caseSensitive = sensitive;
    return this;
  }

  setWholeWordOnly(wholeWord: boolean): FilterRuleBuilder {
    this.rule.wholeWordOnly = wholeWord;
    return this;
  }

  setMinLength(length: number): FilterRuleBuilder {
    this.rule.minLength = length;
    return this;
  }

  setMaxLength(length: number): FilterRuleBuilder {
    this.rule.maxLength = length;
    return this;
  }

  setTimeout(timeout: number): FilterRuleBuilder {
    this.rule.timeout = timeout;
    return this;
  }

  setCacheResults(cache: boolean): FilterRuleBuilder {
    this.rule.cacheResults = cache;
    return this;
  }

  setFieldNames(names: string[]): FilterRuleBuilder {
    this.rule.fieldNames = names;
    return this;
  }

  setFieldExclusions(exclusions: string[]): FilterRuleBuilder {
    this.rule.fieldExclusions = exclusions;
    return this;
  }

  setCreatedBy(author: string): FilterRuleBuilder {
    this.rule.createdBy = author;
    return this;
  }

  build(): FilterRule {
    // Validate required fields
    if (!this.rule.id) throw new Error('FilterRule id is required');
    if (!this.rule.name) throw new Error('FilterRule name is required');
    if (!this.rule.description) throw new Error('FilterRule description is required');
    if (!this.rule.category) throw new Error('FilterRule category is required');
    if (!this.rule.type) throw new Error('FilterRule type is required');
    if (!this.rule.pattern) throw new Error('FilterRule pattern is required');
    if (!this.rule.action) throw new Error('FilterRule action is required');
    if (this.rule.priority === undefined) throw new Error('FilterRule priority is required');
    if (!this.rule.severity) throw new Error('FilterRule severity is required');

    return this.rule as FilterRule;
  }
}

/**
 * Main Filter Engine Implementation
 */
export class FilterEngine implements IFilterEngine {
  private config: FilterEngineConfig;
  private rules = new Map<string, FilterRule>();
  private rulesets = new Map<string, FilterRuleSet>();
  private cache: FilterCache;
  private stats: FilterEngineStats;
  private auditLog: FilterAuditEntry[] = [];
  private ruleVersion = '1.0.0';
  private totalRulesApplied = 0;

  constructor(config?: Partial<FilterEngineConfig>) {
    this.config = this.createDefaultConfig();
    if (config) {
      this.configure(config);
    }

    this.cache = new FilterCache(this.config.cacheMaxSize, this.config.cacheTTL);
    this.stats = this.createDefaultStats();
    
    // Load predefined patterns as default rules
    this.loadPredefinedPatterns();
  }

  private createDefaultConfig(): FilterEngineConfig {
    return {
      enabled: true,
      maxExecutionTime: 5000, // 5 seconds
      maxRuleExecutionTime: 100, // 100ms per rule
      enableCaching: true,
      cacheMaxSize: 1000,
      cacheTTL: 300000, // 5 minutes
      enableParallelProcessing: true,
      maxConcurrentRules: 10,
      failSafe: true,
      logFilterErrors: true,
      rulesets: [],
      customRules: [],
      environmentOverrides: {},
      enableMetrics: true,
      enableAuditLog: false,
      defaultAction: FilterAction.REDACT,
      defaultSeverity: FilterSeverity.MEDIUM
    };
  }

  private createDefaultStats(): FilterEngineStats {
    return {
      totalFiltersApplied: 0,
      totalMatchesFound: 0,
      totalErrorsEncountered: 0,
      averageExecutionTime: 0,
      cacheHitRate: 0,
      performanceBreakdown: {
        ruleExecution: 0,
        resultCaching: 0,
        dataProcessing: 0,
        other: 0
      },
      ruleStats: {},
      categoryStats: {
        [SensitiveDataCategory.PII]: { matches: 0, applications: 0 },
        [SensitiveDataCategory.AUTHENTICATION]: { matches: 0, applications: 0 },
        [SensitiveDataCategory.FINANCIAL]: { matches: 0, applications: 0 },
        [SensitiveDataCategory.MEDICAL]: { matches: 0, applications: 0 },
        [SensitiveDataCategory.CUSTOM]: { matches: 0, applications: 0 }
      }
    };
  }

  private loadPredefinedPatterns(): void {
    const predefinedPatterns = getAllPredefinedPatterns();
    
    for (const [category, patterns] of Object.entries(predefinedPatterns)) {
      for (const pattern of patterns) {
        const rule: FilterRule = {
          id: pattern.id,
          name: pattern.name,
          description: pattern.description,
          category: pattern.category,
          type: FilterRuleType.REGEX,
          pattern: pattern.regex,
          action: FilterAction.REDACT,
          priority: FilterPriority.MEDIUM,
          severity: FilterSeverity.HIGH,
          enabled: true,
          tags: ['predefined', category.toLowerCase()],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          cacheResults: true
        };
        
        this.rules.set(rule.id, rule);
      }
    }
  }

  // Configuration methods
  configure(config: Partial<FilterEngineConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Update cache settings if changed
    if (config.cacheMaxSize || config.cacheTTL) {
      this.cache = new FilterCache(this.config.cacheMaxSize, this.config.cacheTTL);
    }
  }

  getConfig(): FilterEngineConfig {
    return { ...this.config };
  }

  // Rule management methods
  addRule(rule: FilterRule): void {
    this.validateRule(rule);
    this.rules.set(rule.id, rule);
    this.updateRuleVersion();
  }

  removeRule(ruleId: string): void {
    this.rules.delete(ruleId);
    this.updateRuleVersion();
  }

  updateRule(ruleId: string, updates: Partial<FilterRule>): void {
    const existingRule = this.rules.get(ruleId);
    if (!existingRule) {
      throw new Error(`Rule with id '${ruleId}' not found`);
    }

    const updatedRule = {
      ...existingRule,
      ...updates,
      updatedAt: new Date().toISOString()
    };

    this.validateRule(updatedRule);
    this.rules.set(ruleId, updatedRule);
    this.updateRuleVersion();
  }

  getRule(ruleId: string): FilterRule | undefined {
    return this.rules.get(ruleId);
  }

  getRules(): FilterRule[] {
    return Array.from(this.rules.values());
  }

  // Ruleset management methods
  addRuleSet(ruleset: FilterRuleSet): void {
    this.rulesets.set(ruleset.id, ruleset);
    
    // Add rules from ruleset
    for (const rule of ruleset.rules) {
      this.addRule(rule);
    }
  }

  removeRuleSet(rulesetId: string): void {
    const ruleset = this.rulesets.get(rulesetId);
    if (ruleset) {
      // Remove rules from this ruleset
      for (const rule of ruleset.rules) {
        this.removeRule(rule.id);
      }
      this.rulesets.delete(rulesetId);
    }
  }

  getRuleSet(rulesetId: string): FilterRuleSet | undefined {
    return this.rulesets.get(rulesetId);
  }

  getRuleSets(): FilterRuleSet[] {
    return Array.from(this.rulesets.values());
  }

  // Main filtering methods
  filter(data: Record<string, any>, context?: Partial<FilterContext>): FilterResult {
    const startTime = performance.now();
    
    if (!this.config.enabled) {
      return {
        success: true,
        originalData: data,
        filteredData: data,
        matches: [],
        errors: [],
        totalExecutionTime: 0,
        rulesApplied: 0,
        fieldsProcessed: 0,
        stats: {
          redacted: 0,
          masked: 0,
          removed: 0,
          hashed: 0,
          tokenized: 0
        }
      };
    }

    const result: FilterResult = {
      success: true,
      originalData: { ...data },
      filteredData: { ...data },
      matches: [],
      errors: [],
      totalExecutionTime: 0,
      rulesApplied: 0,
      fieldsProcessed: 0,
      stats: {
        redacted: 0,
        masked: 0,
        removed: 0,
        hashed: 0,
        tokenized: 0
      }
    };

    try {
      // Process all fields recursively
      this.processObject(result.filteredData, '', result, context);
      
    } catch (error) {
      result.success = false;
      result.errors.push({
        ruleId: 'filter-engine',
        ruleName: 'Filter Engine',
        fieldPath: 'root',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        executionTime: 0,
        timestamp: new Date().toISOString()
      });
    }

    result.totalExecutionTime = performance.now() - startTime;
    
    // Update statistics after totalExecutionTime is calculated
    this.updateStats(result);
    
    return result;
  }

  private processObject(
    obj: any, 
    basePath: string, 
    result: FilterResult, 
    context?: Partial<FilterContext>,
    depth = 0
  ): void {
    // Prevent infinite recursion
    if (depth > 10) return;

    if (obj === null || typeof obj !== 'object') {
      return;
    }

    if (Array.isArray(obj)) {
      for (let i = 0; i < obj.length; i++) {
        const itemPath = `${basePath}[${i}]`;
        if (typeof obj[i] === 'object') {
          this.processObject(obj[i], itemPath, result, context, depth + 1);
        } else {
          this.filterField(obj, i.toString(), obj[i], itemPath, result, context);
        }
      }
    } else {
      for (const [key, value] of Object.entries(obj)) {
        const fieldPath = basePath ? `${basePath}.${key}` : key;
        result.fieldsProcessed++;

        if (typeof value === 'object' && value !== null) {
          this.processObject(value, fieldPath, result, context, depth + 1);
        } else {
          this.filterField(obj, key, value, fieldPath, result, context);
        }
      }
    }
  }

  private filterField(
    parentObj: any,
    fieldName: string,
    value: any,
    fieldPath: string,
    result: FilterResult,
    context?: Partial<FilterContext>
  ): void {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return;
    }

    const matches = this.filterValue(value, fieldPath, context);
      result.rulesApplied += matches.length;
    result.matches.push(...matches);

    // Apply the highest priority match
    if (matches.length > 0) {
      const highestPriorityMatch = matches.reduce((highest, current) => 
        this.getFilterPriorityValue(current) > this.getFilterPriorityValue(highest) ? current : highest
      );

      // Apply the filter action
      const filteredValue = this.applyFilterAction(highestPriorityMatch);
      parentObj[fieldName] = filteredValue;

      // Update stats
      this.updateActionStats(result.stats, highestPriorityMatch.action);

      // Add to audit log if enabled
      if (this.config.enableAuditLog) {
        this.addAuditEntry(highestPriorityMatch, fieldPath, value, filteredValue, context);
      }
    }
  }

  filterValue(value: any, fieldPath: string, context?: Partial<FilterContext>): FilterMatch[] {
    const matches: FilterMatch[] = [];
    const stringValue = String(value);
    
    // Check cache first
    const cacheKey = this.createCacheKey(stringValue, fieldPath);
    if (this.config.enableCaching) {
      const cachedResult = this.cache.get(cacheKey);
      if (cachedResult) {
        return cachedResult;
      }
    }

    // Get enabled rules sorted by priority
    const enabledRules = Array.from(this.rules.values())
      .filter(rule => rule.enabled)
      .sort((a, b) => b.priority - a.priority);

    for (const rule of enabledRules) {
      
      try {
        const ruleStartTime = performance.now();
        const match = this.applyRule(rule, stringValue, fieldPath, context);
        const ruleExecutionTime = performance.now() - ruleStartTime;

        if (match) {
          match.executionTime = ruleExecutionTime;
          matches.push(match);
          
          // Update rule stats
          this.updateRuleStats(rule.id, true, ruleExecutionTime);
          this.updateCategoryStats(rule.category, true);
        } else {
          this.updateRuleStats(rule.id, false, ruleExecutionTime);
          this.updateCategoryStats(rule.category, false);
        }

        // Check timeout
        if (ruleExecutionTime > (rule.timeout || this.config.maxRuleExecutionTime)) {
          throw new Error(`Rule '${rule.id}' exceeded timeout`);
        }

      } catch (error) {
        this.handleRuleError(rule, fieldPath, error, matches);
      }
    }

    // Cache results if enabled
    if (this.config.enableCaching && matches.length > 0) {
      this.cache.set(cacheKey, matches, this.ruleVersion);
    }

    return matches;
  }

  private applyRule(
    rule: FilterRule,
    value: string,
    fieldPath: string,
    context?: Partial<FilterContext>
  ): FilterMatch | null {
    // Check field restrictions
    if (rule.fieldNames && !rule.fieldNames.some(name => fieldPath.includes(name))) {
      return null;
    }

    if (rule.fieldExclusions && rule.fieldExclusions.some(name => fieldPath.includes(name))) {
      return null;
    }

    // Check length restrictions
    if (rule.minLength && value.length < rule.minLength) {
      return null;
    }

    if (rule.maxLength && value.length > rule.maxLength) {
      return null;
    }

    // Apply the pattern matching based on rule type
    let isMatch = false;
    let matchedPattern = '';
    let confidence = 0;

    switch (rule.type) {
      case FilterRuleType.REGEX:
        const regex = rule.pattern instanceof RegExp ? rule.pattern : new RegExp(rule.pattern as string, rule.caseSensitive ? 'g' : 'gi');
        const regexMatch = regex.exec(value);
        if (regexMatch) {
          isMatch = true;
          matchedPattern = regexMatch[0];
          confidence = this.calculateRegexConfidence(regexMatch, value);
        }
        break;

      case FilterRuleType.KEYWORD:
        const keyword = rule.pattern as string;
        const searchValue = rule.caseSensitive ? value : value.toLowerCase();
        const searchKeyword = rule.caseSensitive ? keyword : keyword.toLowerCase();
        
        if (rule.wholeWordOnly) {
          const wordRegex = new RegExp(`\\b${searchKeyword}\\b`, rule.caseSensitive ? 'g' : 'gi');
          isMatch = wordRegex.test(searchValue);
        } else {
          isMatch = searchValue.includes(searchKeyword);
        }
        
        if (isMatch) {
          matchedPattern = keyword;
          confidence = 0.9; // High confidence for keyword matches
        }
        break;

      case FilterRuleType.FUNCTION:
        // Custom function-based filtering (future enhancement)
        break;
    }

    if (isMatch) {
      return {
        ruleId: rule.id,
        ruleName: rule.name,
        category: rule.category,
        severity: rule.severity,
        action: rule.action,
        originalValue: value,
        filteredValue: this.applyFilterAction({
          ruleId: rule.id,
          ruleName: rule.name,
          category: rule.category,
          severity: rule.severity,
          action: rule.action,
          originalValue: value,
          filteredValue: value,
          matchedPattern,
          fieldPath,
          confidence,
          executionTime: 0
        }),
        matchedPattern,
        fieldPath,
        confidence,
        executionTime: 0
      };
    }

    return null;
  }

  private applyFilterAction(match: FilterMatch): any {
    const rule = this.rules.get(match.ruleId);
    const { action, originalValue, matchedPattern } = match;

    switch (action) {
      case FilterAction.REDACT:
        return rule?.replacement || '[REDACTED]';

      case FilterAction.MASK:
        if (rule?.preserveFormat) {
          return this.maskWithFormat(String(originalValue), matchedPattern);
        }
        return '*'.repeat(String(originalValue).length);

      case FilterAction.REMOVE:
        return undefined;

      case FilterAction.HASH:
        return this.hashValue(String(originalValue));

      case FilterAction.TOKENIZE:
        return this.tokenizeValue(String(originalValue));

      default:
        return '[REDACTED]';
    }
  }

  private maskWithFormat(value: string, pattern: string): string {
    // Preserve format while masking (e.g., XXX-XX-XXXX for SSN)
    return value.replace(/[0-9a-zA-Z]/g, '*');
  }

  private hashValue(value: string): string {
    // Simple hash implementation - in production, use crypto
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      const char = value.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return `[HASH:${Math.abs(hash).toString(16)}]`;
  }

  private tokenizeValue(value: string): string {
    // Generate a token for the value
    return `[TOKEN:${Date.now()}-${Math.random().toString(36).substr(2, 9)}]`;
  }

  private calculateRegexConfidence(match: RegExpMatchArray, fullValue: string): number {
    const matchLength = match[0].length;
    const valueLength = fullValue.length;
    
    // Higher confidence for longer matches relative to the full value
    const lengthRatio = matchLength / valueLength;
    return Math.min(0.5 + lengthRatio, 1.0);
  }

  private getFilterPriorityValue(match: FilterMatch): number {
    // Priority is determined by: rule priority + severity + confidence
    const rulePriority = this.rules.get(match.ruleId)?.priority || FilterPriority.MEDIUM;
    const severityWeight = this.getSeverityWeight(match.severity);
    
    return rulePriority * 10 + severityWeight * 5 + match.confidence;
  }

  private getSeverityWeight(severity: FilterSeverity): number {
    switch (severity) {
      case FilterSeverity.LOW: return 1;
      case FilterSeverity.MEDIUM: return 2;
      case FilterSeverity.HIGH: return 3;
      case FilterSeverity.CRITICAL: return 4;
      default: return 2;
    }
  }

  // Utility methods
  private createCacheKey(value: string, fieldPath: string): string {
    return `${fieldPath}:${this.hashValue(value)}`;
  }

  private updateRuleVersion(): void {
    const now = new Date();
    this.ruleVersion = `${now.getFullYear()}.${now.getMonth() + 1}.${now.getDate()}-${now.getTime()}`;
    
    // Clear cache when rules change
    if (this.config.enableCaching) {
      this.cache.clear();
    }
  }

  private updateStats(result: FilterResult): void {
    if (!this.config.enableMetrics) return;

    this.stats.totalFiltersApplied++;
    this.stats.totalMatchesFound += result.matches.length;
    this.stats.totalErrorsEncountered += result.errors.length;

    // Update average execution time
    const totalTime = this.stats.averageExecutionTime * (this.stats.totalFiltersApplied - 1) + result.totalExecutionTime;
    this.stats.averageExecutionTime = totalTime / this.stats.totalFiltersApplied;

    // Update cache hit rate
    this.stats.cacheHitRate = this.cache.getHitRate();

    // Update performance breakdown with execution time data
    const ruleExecutionTime = result.matches.reduce((sum, match) => sum + (match.executionTime || 0), 0);
    this.stats.performanceBreakdown.ruleExecution = 
      (this.stats.performanceBreakdown.ruleExecution * (this.stats.totalFiltersApplied - 1) + ruleExecutionTime) / this.stats.totalFiltersApplied;
    
    // Data processing time is the remaining time not spent on rule execution
    const dataProcessingTime = Math.max(0, result.totalExecutionTime - ruleExecutionTime);
    this.stats.performanceBreakdown.dataProcessing = 
      (this.stats.performanceBreakdown.dataProcessing * (this.stats.totalFiltersApplied - 1) + dataProcessingTime) / this.stats.totalFiltersApplied;
  }

  private updateRuleStats(ruleId: string, matched: boolean, executionTime: number): void {
    if (!this.config.enableMetrics) return;

    if (!this.stats.ruleStats[ruleId]) {
      this.stats.ruleStats[ruleId] = {
        applications: 0,
        matches: 0,
        errors: 0,
        averageExecutionTime: 0
      };
    }

    const ruleStats = this.stats.ruleStats[ruleId];
    ruleStats.applications++;
    if (matched) ruleStats.matches++;
    
    // Update average execution time
    const totalTime = ruleStats.averageExecutionTime * (ruleStats.applications - 1) + executionTime;
    ruleStats.averageExecutionTime = totalTime / ruleStats.applications;
  }

  private updateCategoryStats(category: SensitiveDataCategory, matched: boolean): void {
    if (!this.config.enableMetrics) return;

    this.stats.categoryStats[category].applications++;
    if (matched) {
      this.stats.categoryStats[category].matches++;
    }
  }

  private updateActionStats(stats: FilterResult['stats'], action: FilterAction): void {
    switch (action) {
      case FilterAction.REDACT:
        stats.redacted++;
        break;
      case FilterAction.MASK:
        stats.masked++;
        break;
      case FilterAction.REMOVE:
        stats.removed++;
        break;
      case FilterAction.HASH:
        stats.hashed++;
        break;
      case FilterAction.TOKENIZE:
        stats.tokenized++;
        break;
    }
  }

  private handleRuleError(rule: FilterRule, fieldPath: string, error: any, result: FilterMatch[]): void {
    if (!this.config.failSafe) {
      throw error;
    }

    if (this.config.logFilterErrors) {
      console.error(`Filter rule '${rule.id}' failed on field '${fieldPath}':`, error);
    }

    // Update error stats
    if (this.config.enableMetrics) {
      this.stats.totalErrorsEncountered++;
      if (!this.stats.ruleStats[rule.id]) {
        this.stats.ruleStats[rule.id] = {
          applications: 0,
          matches: 0,
          errors: 0,
          averageExecutionTime: 0
        };
      }
      this.stats.ruleStats[rule.id].errors++;
    }
  }

  private addAuditEntry(
    match: FilterMatch,
    fieldPath: string,
    originalValue: any,
    filteredValue: any,
    context?: Partial<FilterContext>
  ): void {
    const auditEntry: FilterAuditEntry = {
      timestamp: new Date().toISOString(),
      ruleId: match.ruleId,
      action: match.action,
      fieldPath,
      originalValue: String(originalValue),
      filteredValue: String(filteredValue),
      confidence: match.confidence,
      executionTime: match.executionTime,
      environment: context?.environment || 'unknown',
      logLevel: context?.logLevel || 'unknown',
      namespace: context?.namespace || 'unknown'
    };

    this.auditLog.push(auditEntry);

    // Limit audit log size
    if (this.auditLog.length > 10000) {
      this.auditLog = this.auditLog.slice(-5000); // Keep last 5000 entries
    }
  }

  // Testing and validation methods
  testRule(rule: FilterRule, testData: any): FilterMatch[] {
    const testValue = String(testData);
    return this.filterValue(testValue, 'test', {
      environment: 'test',
      logLevel: 'debug',
      namespace: 'test'
    });
  }

  validateRule(rule: FilterRule): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validation
    if (!rule.id) errors.push('Rule ID is required');
    if (!rule.name) errors.push('Rule name is required');
    if (!rule.description) errors.push('Rule description is required');
    if (!rule.category) errors.push('Rule category is required');
    if (!rule.type) errors.push('Rule type is required');
    if (!rule.pattern) errors.push('Rule pattern is required');
    if (!rule.action) errors.push('Rule action is required');
    if (rule.priority === undefined) errors.push('Rule priority is required');
    if (!rule.severity) errors.push('Rule severity is required');

    // Pattern validation
    if (rule.pattern) {
      try {
        if (rule.type === FilterRuleType.REGEX) {
          if (typeof rule.pattern === 'string') {
            new RegExp(rule.pattern);
          }
        }
      } catch (error) {
        errors.push(`Invalid regex pattern: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Length validation
    if (rule.minLength !== undefined && rule.maxLength !== undefined && rule.minLength > rule.maxLength) {
      errors.push('Minimum length cannot be greater than maximum length');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Performance and monitoring methods
  getStats(): FilterEngineStats {
    return { ...this.stats };
  }

  resetStats(): void {
    this.stats = this.createDefaultStats();
  }

  clearCache(): void {
    this.cache.clear();
  }

  // Rule building
  createRuleBuilder(): FilterRuleBuilder {
    return new FilterRuleBuilderImpl();
  }

  // Additional utility methods
  getAuditLog(): FilterAuditEntry[] {
    return [...this.auditLog];
  }

  clearAuditLog(): void {
    this.auditLog = [];
  }

  exportRules(): FilterRule[] {
    return this.getRules();
  }

  importRules(rules: FilterRule[]): void {
    for (const rule of rules) {
      this.addRule(rule);
    }
  }
}

// Export singleton instance
let filterEngineInstance: FilterEngine | null = null;

export function getFilterEngine(config?: Partial<FilterEngineConfig>): FilterEngine {
  if (!filterEngineInstance) {
    filterEngineInstance = new FilterEngine(config);
  }
  return filterEngineInstance;
}

export function resetFilterEngine(): void {
  filterEngineInstance = null;
} 
